import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getComicById } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

const categoryNameMap: Record<string, string> = {
  romance: '恋爱', fantasy: '奇幻', action: '动作', comedy: '搞笑',
  thriller: '悬疑', drama: '剧情', daily: '日常', school: '校园', workplace: '职场',
};

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const found = getComicById(id || '');
      setComic(found || null);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="flex items-center gap-3 text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-xl"></i>
            <span>加载中...</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">漫画未找到</h2>
          <p className="text-foreground-500 mb-6">该漫画可能已被移除或不存在</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩国漫画`,
            author: { '@type': 'Person', name: comic.author },
            description: comic.description,
            datePublished: comic.chapters[0]?.updatedAt || '',
            dateModified: comic.updatedAt,
            url: `https://www.hanmandq.com/comic/${comic.id}`,
            publisher: {
              '@type': 'Organization',
              name: '韩漫大全',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmandq.com/' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://www.hanmandq.com/category/all' },
              { '@type': 'ListItem', position: 3, name: comic.title, item: `https://www.hanmandq.com/comic/${comic.id}` },
            ],
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <section className="py-8 md:py-10 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to="/category/all" className="hover:text-primary-500 transition-colors whitespace-nowrap">分类</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium truncate">{comic.title}</span>
              </nav>

              <div className="flex flex-col md:flex-row gap-8 md:gap-10">
                <div className="flex-shrink-0 w-full md:w-64">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden border border-background-200">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground-900 mb-2">
                    {comic.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-4">
                    作者：{comic.author}
                  </p>

                  <div className="flex items-center gap-4 flex-wrap mb-4">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-star-fill text-amber-500"></i>
                      <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                      <span className="text-xs text-foreground-400">评分</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-eye-line text-foreground-400"></i>
                      <span className="text-sm font-semibold text-foreground-900">
                        {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
                      </span>
                      <span className="text-xs text-foreground-400">阅读</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-booklet-line text-foreground-400"></i>
                      <span className="text-sm font-semibold text-foreground-900">{comic.chapters.length}话</span>
                      <span className="text-xs text-foreground-400">章节</span>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'}`}>
                      {comic.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {comic.categories.map((cat) => (
                      <Link
                        key={cat}
                        to={`/category/${cat}`}
                        className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                      >
                        {categoryNameMap[cat] || cat}
                      </Link>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-xl p-4 md:p-5 mb-6">
                    <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Link
                      to={`/comic/${comic.id}`}
                      className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line"></i>
                      开始阅读
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-10 md:mt-12">
                <h2 className="text-lg md:text-xl font-display font-bold text-foreground-900 mb-4">
                  章节列表
                </h2>
                <div className="border border-background-200 rounded-xl overflow-hidden">
                  {comic.chapters.map((chapter, index) => (
                    <Link
                      key={chapter.id}
                      to={`/comic/${comic.id}`}
                      className={`flex items-center justify-between p-4 hover:bg-background-100 transition-colors ${
                        index < comic.chapters.length - 1 ? 'border-b border-background-200' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {chapter.number}
                        </span>
                        <span className="text-sm font-medium text-foreground-900">
                          {chapter.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {chapter.updatedAt}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}